/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getImageUrl } from "@Utils";
import { useTranslation, TranslatedString } from "@i18n";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useEffect, useState, type ComponentProps } from "react";
import { getScheduleDetails } from "../../../../actions/task";
import ScheduleDetailsContent from "./ScheduleDetailsContent/ScheduleDetailsContent";
import { convertDate } from "../../../../services/luxonHelper";
import { BsThreeDotsVertical } from "react-icons/bs";
import ForwardArrowIcon from "../../../../assets/icons/svg/ForwardArrow.svg";
import BackwardArrowIcon from "../../../../assets/icons/svg/BackwardArrow.svg";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import UserNameTagComponent from "../../../../cubeComponent/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import { AccordionSummary, AccordionDetails, Box } from "@mui/material";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ModuleUsersAccess from "@Components/ModuleUsersAccess";
import { defaultModulesMapper } from "@Helpers/constants";
import ScheduleVersionsContent from "./ScheduleDetailsContent/ScheduleVersionsContent";
import { ArrowDirection, ScheduleDetailsSidebarProps } from "@WebTypes/scheduleDomain";
import { RootState } from "src/reducers";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import Text from "@CubeComponents/html/Text/Text";

/** One entry of the local `sideBarHeaders` list that drives the accordion sections. */
type SidebarSection = {
  name: TranslatedString;
  icon: string;
  id: string;
  accordionState: string;
};

function ScheduleDetailsSidebar({
  ganttId,
  selectedCount = 0,
  openSideBarWithArrowNavigation,
  setScheduleSideBar,
}: Readonly<ScheduleDetailsSidebarProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const GanttDetails = useSelector((state: RootState) => state.GanttDetails);

  const accordionInitialState: Record<string, boolean> = {
    openDetails: true,
    openAccess: true,
    openVersion: true,
  };

  const [accordionState, setAccordionState] = useState(accordionInitialState);

  const handleSingleAccordionStateUpdate = (type: string) => {
    setAccordionState({
      ...accordionState,
      [type]: !accordionState[type],
    });
  };

  const handleIconButtonClick = (type: string) => {
    setAccordionState(
      Object.fromEntries(
        Object.keys(accordionInitialState).map((key) => [key, key === type ? !accordionState[type] : false]),
      ),
    );
  };

  useEffect(() => {
    if (ganttId) dispatch(getScheduleDetails({ scheduleId: ganttId }));
    if (openSideBarWithArrowNavigation) {
      openSideBarWithArrowNavigation();
    }
  }, [ganttId]);

  const addedByTemplate = () => {
    const _id = GanttDetails.createdBy?._id;
    return <UserNameTagComponent id={_id} size="XS" chipBackground="#6BBBE340" />;
  };

  const handleArrowClick = (direction: ArrowDirection) => {
    if (openSideBarWithArrowNavigation) {
      openSideBarWithArrowNavigation(direction);
    }
  };

  const handleSelectedOption = (args: SelectEventArgs) => {
    if (args.item.properties.text === "Close" && setScheduleSideBar) {
      setScheduleSideBar(false);
    }
  };

  const accordionHeader = (section: SidebarSection) => (
    <div className="flex justify-between w-full p-3 items-center">
      <div className="flex font-semibold gap-2 w-full">
        <IconComponent name={section.icon} width="1.5rem" height="1.5rem" />
        {section.name}
      </div>
    </div>
  );

  const accordionContent = (section: SidebarSection) => {
    if (section.id == "details") {
      return <ScheduleDetailsContent scheduleData={GanttDetails} />;
    } else if (section.id == "access") {
      return (
        <ModuleUsersAccess
          module={defaultModulesMapper.SCHEDULES}
          resourceId={GanttDetails._id ?? ""}
          users={(GanttDetails?.users ?? []) as ComponentProps<typeof ModuleUsersAccess>["users"]}
          spaceId={GanttDetails?.space?._id}
        />
      );
    } else if (section.id == "version") {
      return <ScheduleVersionsContent scheduleId={ganttId as string} />;
    }
    return <></>;
  };

  const sideBarHeaders: SidebarSection[] = [
    {
      name: t("Details"),
      icon: "details",
      id: "details",
      accordionState: "openDetails",
    },
    {
      name: t("Access"),
      icon: "access",
      id: "access",
      accordionState: "openAccess",
    },
    {
      name: t("Versions"),
      icon: "VersionsIcon",
      id: "version",
      accordionState: "openVersion",
    },
  ];

  const getAccordions = () =>
    sideBarHeaders.map((section) => (
      <div key={section.id} className="border-b border-stroke_primary_light pb-3 pe-2">
        <Accordion
          expanded={accordionState[section.accordionState]}
          onChange={() => handleSingleAccordionStateUpdate(section.accordionState)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{accordionHeader(section)}</AccordionSummary>
          <AccordionDetails>{accordionContent(section)}</AccordionDetails>
        </Accordion>
      </div>
    ));

  const rightSidebarIconButton = (action: string, name: string, toolTipContent: TranslatedString) => (
    <ButtonComponent
      className="mt-6 cursor-pointer bg-transparent border-none p-0 focus:outline-none"
      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      onClick={() => handleIconButtonClick(action)}
    >
      <IconComponent name={name} toolTipContent={toolTipContent} width="1.5rem" height="1.5rem" />
    </ButtonComponent>
  );

  return selectedCount === 1 ? (
    <div className="bg-white flex h-full w-full border border-stroke_primary_light rounded-lg overflow-hidden">
      <Box className="flex h-full w-full flex-col overflow-auto bg-[var(--surface-white)] border-e border-stroke_primary_light">
        <div className="p-4 leading-[2.5] border-b border-stroke_primary_light bg-[#FAFCFF]">
          <div className="flex items-center justify-between gap-5">
            <div className="flex items-center gap-2.5 text-base text-[#192048] min-w-0">
              <Text># {`SCH- ${String(GanttDetails.counter).padStart(4, "0")}`}</Text>
              <Text className="text-xl font-semibold">{GanttDetails.title}</Text>
            </div>
            <div className="me-2.5 inline-flex flex-row gap-2.5">
              <img
                alt="backward"
                src={BackwardArrowIcon}
                className="mt-[-0.4375rem] cursor-pointer"
                onClick={() => {
                  handleArrowClick("backward");
                }}
              />
              <img
                alt="forward"
                src={ForwardArrowIcon}
                className="mt-[-0.4375rem] cursor-pointer"
                onClick={() => {
                  handleArrowClick("forward");
                }}
              />
              <SelectDropDown
                items={[{ text: "Close" }]}
                select={handleSelectedOption}
                cssClass="e-caret-hide border-none bg-white p-0"
              >
                <BsThreeDotsVertical />
              </SelectDropDown>
            </div>
          </div>

          <div className="w-full flex justify-between items-center mt-3">
            <div className="w-fit flex flex-row items-center gap-2 border border-gradient_color rounded-full bg-[#6BBBE340">
              {addedByTemplate()}
            </div>
            <div className="text-text_light">{convertDate(Number(GanttDetails.createdDate), "dateForText")}</div>
          </div>
        </div>
        <div className="flex w-full">
          <div className="flex flex-col flex-1 min-w-0">{getAccordions()}</div>
          <div className="px-3 flex flex-col flex-shrink-0 justify-start items-center bg-surface_white border-s border-stroke_primary_light min-h-screen">
            {rightSidebarIconButton("openDetails", "details", t("Details"))}
            {rightSidebarIconButton("openAccess", "access", t("Access"))}
            {rightSidebarIconButton("openVersion", "VersionsIcon", t("Versions"))}
          </div>
        </div>
      </Box>
    </div>
  ) : selectedCount === 0 ? (
    <div className="flex w-full items-center justify-center flex-col bg-white">
      <img src={`${getImageUrl()}/NoData.svg`} />
      {t("No Schedule Selected")}
    </div>
  ) : (
    <div className="flex w-full items-center justify-center flex-col bg-white">
      <img src={`${getImageUrl()}/NoData.svg`} />
      {t("Multiple Schedules Selected")}
    </div>
  );
}

export default ScheduleDetailsSidebar;
